import React from "react";

const ONLINE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function isOnline(lastSeenAt) {
  if (!lastSeenAt) return false;
  try {
    const last = new Date(lastSeenAt).getTime();
    if (Number.isNaN(last)) return false;
    return Date.now() - last < ONLINE_WINDOW_MS;
  } catch {
    return false;
  }
}

const SIZE_MAP = {
  xs: { box: "w-6 h-6", text: "text-[10px]", dot: "w-1.5 h-1.5", ring: "border-[1.5px]" },
  sm: { box: "w-8 h-8", text: "text-xs", dot: "w-2 h-2", ring: "border-2" },
  md: { box: "w-10 h-10", text: "text-sm", dot: "w-2.5 h-2.5", ring: "border-2" },
  lg: { box: "w-14 h-14", text: "text-lg", dot: "w-3 h-3", ring: "border-[3px]" },
  xl: { box: "w-24 h-24", text: "text-3xl", dot: "w-4 h-4", ring: "border-[3px]" },
};

/**
 * Avatar — universal user avatar with online dot + initials fallback.
 *
 * Props:
 *  - user: { name?, artistName?, avatarUrl?, lastSeenAt? } (or null)
 *  - size: "xs"|"sm"|"md"|"lg"|"xl"
 *  - showDot: boolean — whether to draw the online green-dot
 *  - ringColor: tailwind class for the green-dot border (defaults dark navy)
 *  - testIdPrefix: passes through into data-testid
 */
export default function Avatar({
  user,
  size = "md",
  showDot = false,
  ringColor = "border-[#0b1535]",
  testIdPrefix = "avatar",
}) {
  const cfg = SIZE_MAP[size] || SIZE_MAP.md;
  const display = user?.artistName || user?.name || "?";
  const initials = getInitials(display);
  const online = showDot && isOnline(user?.lastSeenAt);

  return (
    <div
      className={`relative inline-block ${cfg.box} rounded-full flex-shrink-0`}
      data-testid={`${testIdPrefix}-root`}
    >
      {user?.avatarUrl ? (
        <img
          src={user.avatarUrl}
          alt={display}
          className={`${cfg.box} rounded-full object-cover bg-[#1a2238]`}
          data-testid={`${testIdPrefix}-img`}
        />
      ) : (
        <div
          className={`${cfg.box} rounded-full bg-[#1a2238] flex items-center justify-center font-bold tracking-wide ${cfg.text}`}
          style={{ color: "#f5c14b" }}
          data-testid={`${testIdPrefix}-initials`}
        >
          {initials}
        </div>
      )}
      {online && (
        <span
          className={`absolute bottom-0 right-0 ${cfg.dot} rounded-full bg-emerald-400 ${cfg.ring} ${ringColor}`}
          data-testid={`${testIdPrefix}-online-dot`}
          aria-label="Online"
          title="Online"
        />
      )}
    </div>
  );
}

export { getInitials, isOnline };
